import { useEffect, useState } from 'react';

type MediaClientLike = {
	mediaStore: {
		getImage: (
			mediaFileId: string,
			options: {
				collection?: string;
				width: number;
				height: number;
				mode: 'fit';
			},
		) => Promise<Blob>;
	};
};

type UseExportThumbnailOptions = {
	isDisplayMode: boolean;
	mediaClient: MediaClientLike | null | undefined;
	mediaFileId: string | null;
	collectionName: string | null;
};

export const useExportThumbnail = ({
	isDisplayMode,
	mediaClient,
	mediaFileId,
	collectionName,
}: UseExportThumbnailOptions): string | null => {
	const [thumbnailObjectUrl, setThumbnailObjectUrl] = useState<string | null>(null);

	useEffect(() => {
		if (isDisplayMode || !mediaClient || !mediaFileId) {
			return;
		}

		let objectUrl: string | null = null;
		let cancelled = false;

		mediaClient.mediaStore
			.getImage(mediaFileId, {
				collection: collectionName ?? undefined,
				width: 800,
				height: 600,
				mode: 'fit',
			})
			.then((blob: Blob) => {
				// If the component unmounted, revoke the blob immediately instead of setting state.
				if (cancelled) {
					objectUrl = URL.createObjectURL(blob);
					URL.revokeObjectURL(objectUrl);
					return;
				}

				objectUrl = URL.createObjectURL(blob);
				setThumbnailObjectUrl(objectUrl);
			})
			.catch(() => {
				// Silently fail; caller can fall through to DocumentViewer.
			});

		return () => {
			cancelled = true;
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
			// Reset state to null to prevent rendering a revoked blob URL
			// when isDisplayMode transitions to true or other dependencies change.
			setThumbnailObjectUrl(null);
		};
	}, [isDisplayMode, mediaClient, mediaFileId, collectionName]);

	return thumbnailObjectUrl;
};
